import { toast } from "sonner";
import { adapter } from "@/services/db";
import { forLog, toPlain, fromPlain } from "@/lib/fieldCodec";

export const SENSITIVE_AUDIT = "cc_sensitive_audit";
const ABSENT = "__absent__";

// Full-fidelity serialisation used for undo (no truncation).
export const ser = (v) => (v === undefined ? ABSENT : JSON.stringify(toPlain(v)));
export const deser = (s) => (s === ABSENT || s == null ? undefined : fromPlain(JSON.parse(s)));

const build = (user, { path, id, action, field = null, before, after, sensitive, extra = {} }) => {
  const now = new Date().toISOString();
  return {
    app: "ops", action, change_type: action, entity_type: path, entity_id: id, field,
    before: sensitive ? "[redacted]" : forLog(before), after: sensitive ? "[redacted]" : forLog(after),
    ...(sensitive ? { sensitive_audit: true } : { before_full: ser(before), after_full: ser(after) }),
    description: `${action} ${path}/${id}${field ? ` · ${field}` : ""}`,
    status: "applied", performed_by: user?.email || null, performed_by_role: user?.role || null,
    changed_by: user?.email || null, created_at: now, server_ts: now, ...extra,
  };
};
// Sensitive docs: full values only in the admin-only cc_sensitive_audit collection.
const sensitiveCopy = (e, before, after) => ({ ...e, before: null, after: null, sensitive_audit: false, before_full: ser(before), after_full: ser(after) });

export async function logEdit(user, entry) {
  const e = build(user, entry);
  try { if (entry.sensitive) await adapter.add(SENSITIVE_AUDIT, sensitiveCopy(e, entry.before, entry.after)); }
  catch (err) { console.warn("sensitive audit failed (publish super-admin rule)", err.code); }
  try { await adapter.add("activity_logs", e); } catch (err) { console.warn("audit log failed", err.code); }
}

export async function logEdits(user, entries) {
  const built = entries.map((x) => build(user, x));
  try {
    const sens = entries.map((x, i) => (x.sensitive ? sensitiveCopy(built[i], x.before, x.after) : null)).filter(Boolean);
    if (sens.length) await adapter.bulkAdd(SENSITIVE_AUDIT, sens);
    await adapter.bulkAdd("activity_logs", built);
  } catch (err) { console.warn("bulk audit log failed", err.code); toast.warning("Changes applied, but audit log write failed — undo may be unavailable"); }
}

// Value to restore for an entry; returns { ok, value } (value undefined => field/doc absent).
export function restoreValue(entry) {
  if (entry.action === "add_field" || entry.action === "create_document") return { ok: true, value: undefined };
  if (entry.before_full != null) return { ok: true, value: deser(entry.before_full) };
  if (typeof entry.before === "string" && entry.before !== "[redacted]") {
    try { return { ok: true, value: fromPlain(JSON.parse(entry.before)) }; } catch { return { ok: false }; }
  }
  return { ok: false };
}
export const afterSer = (entry) => entry.after_full ?? (entry.after == null ? ABSENT : entry.after);
